import {
  Badge,
  Box,
  Icon,
  Text,
  type BoxProps,
  type IconName,
  type Omittable,
} from "@salestrak/ui";

export type RailIcon = IconName;

type RailTileProps = Omittable<{
  icon: RailIcon;
  label: string;
  badge?: string;
  active?: boolean;
  /** Compact mode: icon-only, no label. The label still feeds the
   * accessibility name. */
  compact?: boolean;
}> &
  Omit<BoxProps<"button">, "as" | "children">;

/**
 * RailTile — a single icon + label entry in the ModuleRail.
 *
 * `...rest` forwards to the root Box so a tile can anchor an overlay: Base UI
 * triggers take `render={<RailTile … />}` and merge their own onClick /
 * aria-expanded / data-popup-open / ref onto the element they are given. The
 * More tile is the one site that uses it.
 */
export function RailTile({
  icon,
  label,
  badge,
  active = false,
  compact = false,
  ...rest
}: RailTileProps) {
  return (
    <Box<"button">
      align="center"
      aria-label={label}
      as="button"
      fill="width"
      gap="2xs"
      {...rest}
    >
      <Box
        align="center"
        // Active: high-contrast inverted chip (white in dark theme, dark in
        // light). Its foreground token has to ride along so the Icon — which
        // paints with `currentColor` — stays legible against it. Hover
        // (non-active): subtle hover wash.
        className={active ? "bg-primary text-primary-foreground" : undefined}
        height={36}
        hoverBackground={!(active)}
        justify="center"
        position="relative"
        rounded="xl"
        width={36}
      >
        <Icon color={active ? undefined : "muted"} name={icon} size="md" />
        {badge != null && (
          <Box position="absolute" right={-2} top={-4}>
            {/* Default variant is solid primary — emerald bg + on-primary
              * text, matching the themed inbox count badges elsewhere. */}
            <Badge size="sm">{badge}</Badge>
          </Box>
        )}
      </Box>
      {!compact && (
        <Text
          size="2xs"
          truncate
          variant={active ? undefined : "muted"}
          weight={active ? "semibold" : "normal"}
        >
          {label}
        </Text>
      )}
    </Box>
  );
}
